/**
 * QorTrace Methodology — public spec page.
 *
 * Iter62 (2026-05-06) — major revamp following design_guidelines.json:
 *  - 13 sections in the IDE/whitepaper aesthetic
 *  - Threat model expanded BEYOND Shor/Grover to institutional crypto
 *    inventory (HNDL, KMS/HSM, PKI, vendor supply chain, compliance windows)
 *  - New "Why we're different", "Compliance & Standards", "Rigorous Testing
 *    & Validation", "Live Methodology Updates" sections
 *  - All previous content preserved (BTC/EVM/Solana formulas, taxonomy,
 *    9 detectors, tier mapping, citation, version history, references)
 *
 * Methodology version is intentionally tracked via constants so updates
 * are a single-file edit. Older reports stamp `methodology_version` and
 * remain reproducible via /methodology/v<n> (when published).
 */
import React, { useState } from "react";
import {
  Copy, Check, ExternalLink, ShieldAlert, Database, Key, Network,
  Boxes, Clock, Layers, FileCheck2, Sparkles, Lock, GitBranch,
  Bell, BookOpen, AlertTriangle, ScrollText, Activity, Hash,
  Microscope, ChevronRight, Download, Loader2, AlertOctagon, Award,
} from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultCtaBand from "../components/ConsultCtaBand";
import DecryptedHeading from "../components/docs/DecryptedHeading";


import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";

const BACKEND = process.env.REACT_APP_BACKEND_URL;


// ---------------------------------------------------------------------------
// Live-update knobs — bump these to publish a new methodology revision.
// ---------------------------------------------------------------------------
const METHOD_VERSION  = "qortrace-method-v0.2";
const PUBLISHED_DATE  = "2026-05-06";
const NEXT_VERSION    = "qortrace-method-v0.3";
const NEXT_TARGET_ETA = "Q3 2026";

const HERO_STATS = [
  { value: "9",   label: "DETECTORS" },
  { value: "18",  label: "CHAINS" },
  { value: "5",   label: "NIST FIPS ALIGNED" },
  { value: "180+", label: "REGRESSION TESTS" },
];


export default function MethodologyPage() {
  useDocumentTitle('Methodology — QorTrace');
  usePageMeta({
    title: "Methodology — QorTrace",
    description: "How QorTrace scores post-quantum cryptographic exposure across 18 chains — open methodology, signed certificates, reproducible scoring rubric.",
    canonicalPath: "/methodology",
  });
  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-12">

        {/* SECTION 1 — Hero band ------------------------------------------- */}
        <HeroBand />

        {/* SECTION 2 — Why QorTrace is different --------------------------- */}
        <Differentiators />

        {/* SECTION 3 — Threat model (expanded institutional scope) --------- */}
        <ThreatModel />

        {/* SECTION 4 — Compliance & Standards Alignment -------------------- */}
        <ComplianceGrid />

        {/* SECTION 5 — Scoring engine ------------------------------------- */}
        <ScoringEngine />

        {/* SECTION 6 — Address taxonomy ----------------------------------- */}
        <AddressTaxonomy />

        {/* SECTION 7 — Smart-contract detector inventory ------------------ */}
        <DetectorInventory />

        {/* SECTION 8 — Tier mapping --------------------------------------- */}
        <TierMapping />

        {/* SECTION 9 — Rigorous testing & validation ---------------------- */}
        <RigorousTesting />

        {/* SECTION 10 — Live methodology updates -------------------------- */}
        <LiveUpdates />

        {/* SECTION 11 — Methodology compliance receipt ------------------- */}
        <ComplianceReceiptSection />

        {/* SECTION 12 — Citation block ------------------------------------ */}
        <SectionShell num="12" title="Cite this methodology">
          <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
            QorTrace methodology is open and citable. When you reference
            QorTrace in research, audits, board reports, regulatory filings,
            or press, please cite the specific version you used. Older reports
            stamp <code className="qt-mono text-xs"
                       style={{ color: "var(--qt-cyan)" }}>methodology_version</code>
            {" "}and remain reproducible against their stamped revision.
          </p>
          <CitationBlock />
        </SectionShell>

        {/* SECTION 12 — Version history ----------------------------------- */}
        <SectionShell num="12" title="Version history">
          <VersionHistory />
        </SectionShell>

        {/* SECTION 13 — References ---------------------------------------- */}
        <References />

        {/* Phase 14.4 — QorBOM cross-link callout. Procurement /
            Big-4 audit visitors who land on /methodology to verify
            the science behind QorTrace should see that we also
            white-label this exact methodology for audit practices
            under the QorBOM brand. */}
        <section data-testid="methodology-qorbom-callout"
                 style={{
                   marginTop: 64,
                   padding: "28px 32px",
                   background: "rgba(0, 51, 160, 0.05)",
                   border: "1px solid rgba(0, 51, 160, 0.25)",
                   borderLeft: "3px solid var(--qt-cyan)",
                   borderRadius: 6,
                 }}>
          <div className="qt-mono text-[10px] mb-2"
               style={{ color: "var(--qt-cyan)",
                        letterSpacing: "0.14em" }}>
            FOR AUDIT FIRMS · WHITE-LABEL
          </div>
          <h3 className="text-xl font-semibold mb-2"
              style={{ color: "#FFFFFF" }}>
            Run this methodology under your own brand.
          </h3>
          <p className="text-sm leading-relaxed mb-4"
             style={{ color: "var(--qt-text-2)", maxWidth: 720 }}>
            Big-4 and boutique audit firms can license the QorTrace
            scanning engine + CBOM generator as <strong style={{ color: "#FFFFFF" }}>QorBOM™</strong>
            — your tenant, your brand, your client-facing portal,
            issuing reports stamped with the methodology version
            cited on this page. One platform; two go-to-market
            surfaces.
          </p>
          <a href="https://qorbom.com"
             target="_blank"
             rel="noopener noreferrer"
             data-testid="methodology-qorbom-link"
             className="qt-mono text-[11px] inline-flex items-center gap-1.5"
             style={{
               padding: "8px 14px", borderRadius: 4,
               background: "var(--qt-cyan)", color: "#0F172A",
               fontWeight: 700, letterSpacing: "0.06em",
               textDecoration: "none",
             }}>
            EXPLORE QORBOM →
          </a>
        </section>

        <p className="qt-mono text-[11px] mt-12"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
          methodology version <span style={{ color: "var(--qt-cyan)" }}>{METHOD_VERSION}</span> ·
          {" "}canonical specification · stamped reports remain reproducible
          against their methodology version
        </p>
      </div>
      <ConsultCtaBand source="methodology-bottom-cta" variant="card" maxWidth={1100} />
      <Footer />
    </div>
  );
}


// ===========================================================================
// 1. HERO BAND
// ===========================================================================
function HeroBand() {
  return (
    <div data-testid="methodology-hero" className="pt-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span data-testid="hero-version-pill"
              className="qt-mono text-[10px] px-2.5 py-1 rounded"
              style={{ color: "var(--qt-amber)",
                       border: "1px solid rgba(255,176,0,0.40)",
                       background: "rgba(255,176,0,0.08)",
                       letterSpacing: "0.16em", fontWeight: 700 }}>
          {METHOD_VERSION}
        </span>
        <span data-testid="hero-status-pill"
              className="qt-mono text-[10px] px-2.5 py-1 rounded inline-flex items-center gap-1.5"
              style={{ color: "#1FDD83",
                       border: "1px solid rgba(123,217,168,0.40)",
                       background: "rgba(123,217,168,0.08)",
                       letterSpacing: "0.16em", fontWeight: 700 }}>
          <span style={{ width: 6, height: 6, borderRadius: 999,
                         background: "#1FDD83", display: "inline-block" }} />
          OPEN · LIVE
        </span>
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
          PUBLISHED {PUBLISHED_DATE} · UPDATED CONTINUOUSLY
        </span>
      </div>

      <DecryptedHeading
        target="QorTrace Methodology & Architecture"
        className="qt-h2 qt-docs-decrypt text-4xl sm:text-5xl mt-4"
        testId="hero-title"
      />

      <p className="mt-4 max-w-3xl text-base"
         style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
        <strong style={{ color: "#fff" }}>Deterministic, evidence-grounded,
        and NIST-aligned.</strong> Every QorTrace point traces to a specific
        cryptographic fact. The LLM advisor only narrates these findings —
        it cannot invent risk. This page is the canonical public formula.
        We keep it living: as standards evolve and as we adopt new techniques,
        the methodology version bumps and previously-issued reports remain
        pinned to the version they were generated against.
      </p>

      <div className="mt-6 flex items-center gap-x-6 gap-y-3 flex-wrap"
           data-testid="hero-stats">
        {HERO_STATS.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2">
            <span className="qt-mono text-2xl font-bold"
                  style={{ color: "var(--qt-cyan)",
                           letterSpacing: "-0.02em" }}>
              {s.value}
            </span>
            <span className="qt-mono text-[10px]"
                  style={{ color: "var(--qt-text-3)",
                           letterSpacing: "0.16em" }}>
              {s.label}
            </span>
            {i < HERO_STATS.length - 1 && (
              <span className="qt-mono text-base"
                    style={{ color: "var(--qt-border)", marginLeft: 12 }}>·</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ===========================================================================
// 2. WHY QORTRACE IS DIFFERENT
// ===========================================================================
const DIFFERENTIATORS = [
  {
    id: "deterministic",
    Icon: Hash,
    title: "Deterministic Scoring",
    desc: "Rule-based engines, not heuristic black-boxes. Same input, same output, every time. Two independent reviewers reproduce identical scores.",
    accent: "var(--qt-cyan)",
  },
  {
    id: "stamped",
    Icon: GitBranch,
    title: "Stamped & Reproducible",
    desc: "Every report freezes its methodology_version. We can replay 2025 reports with 2025 rules five years later — auditors love this.",
    accent: "#1FDD83",
  },
  {
    id: "peer-reviewable",
    Icon: Microscope,
    title: "Peer-Reviewable",
    desc: "The full formula lives on this page in plain language. Submit corrections via /security or open a public review thread — we publish responses.",
    accent: "var(--qt-cyan)",
  },
  {
    id: "no-llm-hallucination",
    Icon: Lock,
    title: "No LLM Hallucination",
    desc: "Our AI narrator describes findings only. It cannot invent severity, change scores, or bypass detectors. The math is deterministic; the prose explains it.",
    accent: "var(--qt-amber)",
  },
];

function Differentiators() {
  return (
    <SectionShell num="2" title="Why QorTrace is different"
                  testid="section-differentiators">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
        {DIFFERENTIATORS.map(({ id, Icon, title, desc, accent }) => (
          <div key={id}
               data-testid={`diff-card-${id}`}
               className="qt-card group transition-all"
               style={{ padding: 18,
                        background: "rgba(4,9,20,0.5)",
                        borderColor: "var(--qt-border-soft)" }}>
            <Icon size={18} style={{ color: accent }} />
            <h3 className="qt-mono mt-3"
                style={{ color: "#fff", fontWeight: 700,
                         fontSize: 14, letterSpacing: "-0.01em" }}>
              {title}
            </h3>
            <p className="text-[13px] mt-2"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
              {desc}
            </p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}


// ===========================================================================
// 3. THREAT MODEL — INSTITUTIONAL CRYPTO INVENTORY
// ===========================================================================
const THREAT_SURFACES = [
  {
    id: "hndl",
    Icon: Database,
    severity: "Critical",
    title: "Harvest Now, Decrypt Later (HNDL)",
    desc: "State-level adversaries are already capturing TLS / VPN / RPC traffic, signed transactions, and encrypted backups. When a cryptographically-relevant quantum computer (CRQC) arrives, today's RSA-2048 / ECDSA / X25519 ciphertexts become retroactively decryptable. Anything with a long secrecy half-life — IP, M&A, custody key recovery, regulatory filings — is exposed today, even before a CRQC exists.",
    refs: [{ label: "NSA CNSA 2.0 Advisory", url: "https://www.nsa.gov/Press-Room/Press-Releases-Statements/Press-Release-View/Article/3148990/nsa-releases-future-quantum-resistant-qr-algorithm-requirements-for-national-se/" }],
  },
  {
    id: "blockchain",
    Icon: Layers,
    severity: "Critical",
    title: "Blockchain Cryptographic Primitives",
    desc: "ECDSA (BTC/ETH/BSC/Tron), EdDSA (Solana/Stellar/Cosmos), Schnorr (Taproot), and BLS (Ethereum consensus, Chia, ZK rollups) are all broken by Shor's algorithm. Hash functions (SHA-256, Keccak-256) are weakened by Grover's to ~128-bit security. Public-key exposure timing varies by address type — see Section 6.",
    refs: [{ label: "NIST FIPS 204 (ML-DSA)", url: "https://csrc.nist.gov/pubs/fips/204/final" }],
  },
  {
    id: "kms-hsm",
    Icon: Key,
    severity: "High",
    title: "KMS / HSM Key Inventory",
    desc: "Institutions accumulate cryptographic keys across AWS KMS, Azure Key Vault, GCP KMS, on-prem HSMs (Thales, Entrust, AWS CloudHSM), and bespoke key escrow services. Each key has a generation algorithm, key length, rotation cadence, and a downstream blast-radius. We help you inventory which keys protect what, and which ones become quantum-vulnerable on day one of a CRQC.",
    refs: [{ label: "NIST SP 800-57 — Key Management", url: "https://csrc.nist.gov/pubs/sp/800/57/pt1/r5/final" }],
  },
  {
    id: "pki",
    Icon: FileCheck2,
    severity: "High",
    title: "Certificate / PKI Inventory",
    desc: "Enterprise PKI sprawls across internal CAs, public CAs, mTLS certs, code-signing certs, S/MIME, and IoT device identities — each with its own RSA/ECDSA dependency. We map your certificate inventory against PQ-readiness: which certs need hybrid issuance now, which need pure ML-DSA at next rotation, which can wait until 2030.",
    refs: [{ label: "CA/Browser Forum baseline", url: "https://cabforum.org/baseline-requirements-documents/" }],
  },
  {
    id: "vendor-supply-chain",
    Icon: Network,
    severity: "High",
    title: "Vendor & Supply-Chain Cryptography",
    desc: "Your TLS terminator, payment gateway, identity provider, code-signing toolchain, observability stack, and HSM vendor all bring their own cryptographic primitives. A single vendor still on RSA-2048 in 2031 is a single point of HNDL exposure for your entire institution. We track vendor PQ-readiness publicly and integrate roadmap signals into your inventory.",
    refs: [{ label: "ENISA Post-Quantum Cryptography", url: "https://www.enisa.europa.eu/topics/digital-identity-and-data-protection/cryptography" }],
  },
  {
    id: "compliance-windows",
    Icon: Clock,
    severity: "Medium",
    title: "Migration & Compliance Windows",
    desc: "NSA CNSA 2.0 mandates PQ adoption for NSS by 2030 (software/firmware) and 2035 (hardware). FFIEC examiners expect documented crypto-agility plans now. EU DORA (Jan 2025) requires ICT third-party risk reporting. The window to inventory → migrate → re-certify is shorter than vendors admit; we sequence migration so the highest-risk assets move first.",
    refs: [
      { label: "NSA CNSA 2.0 algorithms", url: "https://media.defense.gov/2025/May/30/2003728741/-1/-1/0/CSA_CNSA_2.0_ALGORITHMS.PDF" },
      { label: "EU DORA", url: "https://www.eiopa.europa.eu/digital-operational-resilience-act-dora_en" },
    ],
  },
];

const SEV_TONE = {
  Critical: { color: "#FF6E6E", bg: "rgba(255,110,110,0.10)", border: "rgba(255,110,110,0.40)" },
  High:     { color: "var(--qt-amber)", bg: "rgba(255,176,0,0.10)", border: "rgba(255,176,0,0.40)" },
  Medium:   { color: "#1FDD83", bg: "rgba(123,217,168,0.10)", border: "rgba(123,217,168,0.40)" },
};

function ThreatModel() {
  return (
    <SectionShell num="3" title="Threat model · institutional crypto inventory"
                  testid="section-threat-model">
      <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
        QorTrace assumes the eventual existence of a{" "}
        <em>cryptographically-relevant quantum computer (CRQC)</em> running
        Shor's at scale. But blockchain primitives are only one of six
        institutional threat surfaces we score. The full inventory is broader:
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
        {THREAT_SURFACES.map(({ id, Icon, severity, title, desc, refs }) => {
          const tone = SEV_TONE[severity];
          return (
            <div key={id}
                 data-testid={`threat-card-${id}`}
                 className="qt-card flex flex-col"
                 style={{ padding: 20,
                          background: "rgba(4,9,20,0.55)",
                          borderColor: "var(--qt-border-soft)",
                          borderLeft: `3px solid ${tone.color}` }}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <Icon size={16} style={{ color: tone.color }} />
                  <h3 className="qt-mono"
                      style={{ color: "#fff", fontWeight: 700,
                               fontSize: 14, letterSpacing: "-0.005em" }}>
                    {title}
                  </h3>
                </div>
                <span className="qt-mono text-[10px] px-2 py-0.5 rounded"
                      data-testid={`threat-severity-${id}`}
                      style={{ color: tone.color,
                               border: `1px solid ${tone.border}`,
                               background: tone.bg,
                               letterSpacing: "0.16em", fontWeight: 700 }}>
                  {severity.toUpperCase()}
                </span>
              </div>
              <p className="text-[13px] mt-3"
                 style={{ color: "var(--qt-text-2)", lineHeight: 1.65 }}>
                {desc}
              </p>
              {refs && refs.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {refs.map((r) => (
                    <a key={r.url} href={r.url}
                       target="_blank" rel="noopener noreferrer"
                       data-testid={`threat-ref-${id}`}
                       className="qt-mono text-[10px] inline-flex items-center gap-1 transition-colors"
                       style={{ color: "var(--qt-cyan)",
                                letterSpacing: "0.06em",
                                textDecoration: "underline",
                                textDecorationColor: "rgba(102,224,255,0.30)",
                                textUnderlineOffset: 3 }}>
                      {r.label}
                      <ExternalLink size={9} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 qt-card"
           data-testid="threat-misconception"
           style={{ padding: "16px 20px",
                    background: "rgba(255,176,0,0.06)",
                    borderColor: "rgba(255,176,0,0.30)",
                    borderLeft: "3px solid var(--qt-amber)" }}>
        <div className="qt-label mb-1.5"
             style={{ color: "var(--qt-amber)",
                      letterSpacing: "0.16em" }}>
          COMMON MISCONCEPTION
        </div>
        <p style={{ color: "var(--qt-text-1)",
                    fontSize: 16, lineHeight: 1.65 }}>
          Schnorr / Ed25519 / BLS are{" "}
          <strong style={{ color: "#fff" }}>not</strong> quantum-safe. All
          three rely on the discrete-log problem and are broken by Shor's.
          NIST-finalized replacements (ML-DSA / SLH-DSA / Falcon) are
          operational today; STARKs are PQ-safe by construction.
        </p>
      </div>
    </SectionShell>
  );
}


// ===========================================================================
// 4. COMPLIANCE & STANDARDS ALIGNMENT
// ===========================================================================
const COMPLIANCE_STANDARDS = [
  {
    id: "nist-fips-203",
    label: "NIST FIPS 203",
    sub: "ML-KEM (Kyber) — KEM",
    url: "https://csrc.nist.gov/pubs/fips/203/final",
    map: "Used to identify systems still on RSA / ECDH key exchange.",
  },
  {
    id: "nist-fips-204",
    label: "NIST FIPS 204",
    sub: "ML-DSA (Dilithium) — signatures",
    url: "https://csrc.nist.gov/pubs/fips/204/final",
    map: "Recommended replacement for ECDSA / RSA signatures.",
  },
  {
    id: "nist-fips-205",
    label: "NIST FIPS 205",
    sub: "SLH-DSA (SPHINCS+) — hash-based",
    url: "https://csrc.nist.gov/pubs/fips/205/final",
    map: "Conservative fallback for long-lived signing roots.",
  },
  {
    id: "nsa-cnsa-2",
    label: "NSA CNSA 2.0",
    sub: "Migration timeline 2030/2035",
    url: "https://media.defense.gov/2025/May/30/2003728741/-1/-1/0/CSA_CNSA_2.0_ALGORITHMS.PDF",
    map: "Drives priority sequencing for federal-adjacent inventory.",
  },
  {
    id: "fips-140-3",
    label: "FIPS 140-3",
    sub: "Cryptographic Module Validation",
    url: "https://csrc.nist.gov/projects/cryptographic-module-validation-program",
    map: "Cross-referenced against your HSM / KMS inventory.",
  },
  {
    id: "iso-27001",
    label: "ISO/IEC 27001",
    sub: "Annex A.10 cryptographic controls",
    url: "https://www.iso.org/standard/27001",
    map: "Crypto-inventory feeds the A.10 control review evidence.",
  },
  {
    id: "soc2",
    label: "SOC 2 Type II",
    sub: "CC6.1 / CC6.7 logical access + crypto",
    url: "https://www.aicpa-cima.com/topic/audit-assurance/audit-and-assurance-greater-than-soc-2",
    map: "Crypto-readiness reports map to CC6 trust criteria.",
  },
  {
    id: "ffiec",
    label: "FFIEC IT Handbook",
    sub: "Information Security · Cryptography",
    url: "https://ithandbook.ffiec.gov/it-booklets/information-security/",
    map: "Examiners increasingly request crypto-agility plans.",
  },
  {
    id: "enisa-pqc",
    label: "ENISA PQC",
    sub: "Post-Quantum migration guidance",
    url: "https://www.enisa.europa.eu/topics/digital-identity-and-data-protection/cryptography",
    map: "EU baseline for institutional PQ-readiness.",
  },
  {
    id: "eu-dora",
    label: "EU DORA",
    sub: "Digital Operational Resilience Act",
    url: "https://www.eiopa.europa.eu/digital-operational-resilience-act-dora_en",
    map: "Maps third-party crypto-dependency reporting.",
  },
  {
    id: "bis-basel",
    label: "BIS / Basel III",
    sub: "Operational risk · cryptoassets",
    url: "https://www.bis.org/bcbs/publ/d545.htm",
    map: "Custody-risk weighting includes signature-scheme exposure.",
  },
  {
    id: "bip-340",
    label: "BIP-340",
    sub: "Schnorr signatures over secp256k1",
    url: "https://github.com/bitcoin/bips/blob/master/bip-0340.mediawiki",
    map: "Cited in Taproot / P2TR exposure analysis.",
  },
];

function ComplianceGrid() {
  return (
    <SectionShell num="4" title="Compliance &amp; standards alignment"
                  testid="section-compliance">
      <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
        QorTrace findings map directly onto standards used by auditors,
        examiners, and procurement reviewers. Each alignment is a real
        clause, not a vague reference — click through to the source.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5"
           data-testid="compliance-grid">
        {COMPLIANCE_STANDARDS.map((s) => (
          <a key={s.id} href={s.url}
             target="_blank" rel="noopener noreferrer"
             data-testid={`compliance-link-${s.id}`}
             className="qt-card transition-all hover:opacity-100"
             style={{ padding: 14,
                      background: "rgba(4,9,20,0.55)",
                      borderColor: "var(--qt-border-soft)",
                      display: "block",
                      textDecoration: "none" }}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="qt-mono"
                     style={{ color: "#fff", fontWeight: 700,
                              fontSize: 13, letterSpacing: "-0.005em" }}>
                  {s.label}
                </div>
                <div className="qt-mono text-[11px] mt-0.5"
                     style={{ color: "var(--qt-cyan)",
                              letterSpacing: "0.04em" }}>
                  {s.sub}
                </div>
              </div>
              <ExternalLink size={11} style={{ color: "var(--qt-text-3)",
                                                marginTop: 3 }} />
            </div>
            <p className="text-[12px] mt-2"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
              {s.map}
            </p>
          </a>
        ))}
      </div>
    </SectionShell>
  );
}


// ===========================================================================
// 5. SCORING ENGINE — tabbed BTC / EVM / Solana
// ===========================================================================
const SCORING_TABS = [
  {
    id: "btc",
    label: "Bitcoin family",
    sub: "BTC · LTC · BCH · DOGE",
    rows: [
      ["Public-key exposure",       "0–40", "P2PK / P2TR ⇒ 40. Hash-locked + spent ⇒ 40. Hash-locked + unspent ⇒ 0."],
      ["Address reuse",             "0–15", "min(15, (outgoing_tx − 1) × 3)"],
      ["Balance at risk",           "0–20", "log₁₀(USD) / 6 × 20  · only if exposed"],
      ["Dormancy with exposed key", "0–15", "+15 if exposed and inactive > 365d · +8 if > 180d"],
      ["Address-type modifier",     "0–10", "P2PK +10 · P2TR +5 · others +0"],
    ],
    total: "Total = sum (capped at 100)",
    note: null,
  },
  {
    id: "evm",
    label: "EVM family",
    sub: "ETH · BSC · Base · Polygon · Arbitrum · Optimism · Tron",
    rows: [
      ["Public-key exposure",         "0–50", "Nonce > 0 and not a real contract ⇒ 50. EOA exposes pubkey on first send."],
      ["Balance at risk",             "0–25", "log₁₀(USD) / 6 × 25  · only if exposed"],
      ["Activity volume",             "0–10", "log₁₀(nonce) × 5  · caps at 10"],
      ["Smart-contract mitigation",   "−10",  "Real contract (not EIP-7702 delegated) ⇒ subtract 10."],
      ["Dormancy",                    "0–15", "+15 if exposed and inactive > 365d · +8 if > 180d"],
    ],
    total: "Total = sum, clamped to [0, 100]",
    note: (
      <>
        <strong style={{ color: "#fff" }}>EIP-7702 delegation</strong>{" "}
        (Pectra, May 2025): an EOA can carry delegation code starting with
        the magic prefix{" "}
        <span className="qt-mono" style={{ color: "var(--qt-cyan)" }}>
          0xef0100
        </span>. We treat these as cryptographic EOAs — the underlying
        private key still signs every transaction.
      </>
    ),
  },
  {
    id: "solana",
    label: "Solana",
    sub: "Ed25519 native",
    rows: [
      ["Address-as-pubkey exposure", "60",   "Always full — the address literally is the public key."],
      ["Balance at risk",            "0–25", "log₁₀(SOL_USD) / 6 × 25"],
      ["Dormancy",                   "0–15", "+15 if active and idle > 365d · +8 if > 180d · baseline 5"],
    ],
    total: "Total = sum, clamped to [0, 100]",
    note: (
      <>On Solana, every address <strong style={{ color: "#fff" }}>is</strong>{" "}
      the Ed25519 public key (base58-encoded). Public-key exposure is
      structural and permanent, regardless of activity.</>
    ),
  },
];

function ScoringEngine() {
  const [tab, setTab] = useState("btc");
  const active = SCORING_TABS.find((t) => t.id === tab) || SCORING_TABS[0];
  return (
    <SectionShell num="5" title="Scoring engine · per-chain formulas"
                  testid="section-scoring-engine">
      <div className="flex flex-wrap gap-1.5 mb-4"
           data-testid="scoring-tabs">
        {SCORING_TABS.map((t) => (
          <button key={t.id}
                  data-testid={`scoring-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="qt-mono text-[11px] px-3 py-1.5 transition-all"
                  style={{ background: tab === t.id
                                       ? "rgba(102,224,255,0.10)"
                                       : "transparent",
                           color: tab === t.id
                                  ? "var(--qt-cyan)" : "var(--qt-text-3)",
                           border: tab === t.id
                                   ? "1px solid var(--qt-cyan)"
                                   : "1px solid var(--qt-border)",
                           letterSpacing: "0.10em",
                           borderRadius: 4, fontWeight: 700,
                           cursor: "pointer" }}>
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="qt-mono text-[10px] mb-3"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
        SCOPE · {active.sub.toUpperCase()}
      </div>
      <Formula rows={active.rows} total={active.total} />
      {active.note && (
        <p className="mt-3 text-[13px]"
           style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
          {active.note}
        </p>
      )}
    </SectionShell>
  );
}


// ===========================================================================
// 6. ADDRESS TAXONOMY
// ===========================================================================
function AddressTaxonomy() {
  return (
    <SectionShell num="6" title="Address-type taxonomy · Bitcoin family"
                  testid="section-address-taxonomy">
      <Table
        head={["Type", "Pubkey on-chain", "When exposed", "Risk class"]}
        rows={[
          ["P2PK (Satoshi-era)", "Yes, always",   "Always",         "Critical"],
          ["P2PKH (1…)",         "Hash only",     "On first spend", "High after spend"],
          ["P2SH (3…)",          "Depends",       "On spend",       "Variable"],
          ["P2WPKH (bc1q…)",     "Hash only",     "On first spend", "Same as P2PKH"],
          ["P2TR (bc1p…)",       "Yes (x-only)",  "At creation",    "High — Schnorr broken"],
        ]}
      />
    </SectionShell>
  );
}


// ===========================================================================
// 7. SMART-CONTRACT DETECTOR INVENTORY (with example traces)
// ===========================================================================
const DETECTORS = [
  { id: "ecrecover_usage",    sev: "High",     desc: "Direct ecrecover ECDSA signature verification.",
    catches: "v, r, s = ecrecover(msgHash, ...)" },
  { id: "eip712_signing",     sev: "High",     desc: "EIP-712 typed-data signing patterns.",
    catches: "DOMAIN_SEPARATOR · _hashTypedDataV4(...)" },
  { id: "permit_pattern",     sev: "High",     desc: "ERC-2612 ECDSA permit() function.",
    catches: "permit(owner, spender, value, deadline, v, r, s)" },
  { id: "bls_precompile",     sev: "Critical", desc: "BLS / pairing precompile (0x08, 0x0a, EIP-2537).",
    catches: "staticcall(0x08, ...) · pairing()" },
  { id: "hardcoded_signer",   sev: "High",     desc: "Constant SIGNER / VERIFIER / ORACLE address.",
    catches: "address constant SIGNER = 0x...;" },
  { id: "multisig_threshold", sev: "Medium",   desc: "Threshold / Safe / requireMultiSig patterns.",
    catches: "require(threshold ≤ owners.length)" },
  { id: "replay_protection",  sev: "Info",     desc: "Nonce, deadline, chainId, DOMAIN_SEPARATOR present.",
    catches: "nonces[user]++ · block.chainid" },
  { id: "upgradeable_proxy",  sev: "Info",     desc: "UUPS / Transparent / Beacon / _authorizeUpgrade.",
    catches: "_authorizeUpgrade(address newImpl)" },
  { id: "eip1271_signer",     sev: "Medium",   desc: "isValidSignature(bytes32, bytes) modular signer.",
    catches: "isValidSignature(bytes32, bytes) → 0x1626ba7e" },
];

const DETECTOR_SEV = {
  Critical: { color: "#FF6E6E" },
  High:     { color: "var(--qt-amber)" },
  Medium:   { color: "#1FDD83" },
  Info:     { color: "var(--qt-text-3)" },
};

function DetectorInventory() {
  return (
    <SectionShell num="7" title="Smart-contract detector inventory"
                  testid="section-detectors">
      <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
        Nine deterministic detectors run against every contract submission.
        Each fires on a specific Solidity / EVM bytecode pattern and emits
        a default severity that operators can override per-engagement.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-5">
        {DETECTORS.map((d) => {
          const tone = DETECTOR_SEV[d.sev] || DETECTOR_SEV.Info;
          return (
            <div key={d.id}
                 data-testid={`detector-card-${d.id}`}
                 className="qt-card"
                 style={{ padding: 16,
                          background: "rgba(4,9,20,0.55)",
                          borderColor: "var(--qt-border-soft)" }}>
              <div className="flex items-center justify-between gap-3">
                <code className="qt-mono"
                      style={{ color: "var(--qt-cyan)",
                               fontWeight: 700, fontSize: 13 }}>
                  {d.id}
                </code>
                <span className="qt-mono text-[10px] px-2 py-0.5 rounded"
                      data-testid={`detector-sev-${d.id}`}
                      style={{ color: tone.color,
                               border: `1px solid ${tone.color}66`,
                               letterSpacing: "0.16em",
                               fontWeight: 700 }}>
                  {d.sev.toUpperCase()}
                </span>
              </div>
              <p className="text-[13px] mt-2"
                 style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
                {d.desc}
              </p>
              <pre className="qt-mono text-[11px] mt-2 p-2 rounded overflow-x-auto"
                   style={{ background: "rgba(0,0,0,0.40)",
                            border: "1px solid var(--qt-border-soft)",
                            color: "var(--qt-text-2)",
                            margin: 0 }}>
{d.catches}
              </pre>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}


// ===========================================================================
// 8. TIER MAPPING
// ===========================================================================
const TIERS = [
  { range: "0 – 25",   tier: "Low",      desc: "Minimal exposure. Monitor.",                                    color: "#1FDD83" },
  { range: "26 – 50",  tier: "Moderate", desc: "Plan migration within 24 months.",                              color: "#FFB000" },
  { range: "51 – 75",  tier: "High",     desc: "Migrate within 12 months. Consider hybrid signing.",            color: "#FF8B40" },
  { range: "76 – 100", tier: "Critical", desc: "Migrate immediately. Move funds to a fresh, unspent address.",  color: "#FF6E6E" },
];

function TierMapping() {
  return (
    <SectionShell num="8" title="Tier mapping" testid="section-tier-mapping">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        {TIERS.map((t) => (
          <div key={t.tier}
               data-testid={`tier-card-${t.tier.toLowerCase()}`}
               className="qt-card"
               style={{ padding: 14,
                        background: "rgba(4,9,20,0.55)",
                        borderTop: `3px solid ${t.color}`,
                        borderColor: "var(--qt-border-soft)" }}>
            <div className="qt-mono text-[10px]"
                 style={{ color: "var(--qt-text-3)",
                          letterSpacing: "0.16em" }}>
              SCORE
            </div>
            <div className="qt-mono text-2xl"
                 style={{ color: "#fff", fontWeight: 700,
                          letterSpacing: "-0.02em" }}>
              {t.range}
            </div>
            <div className="qt-mono text-xs mt-1"
                 style={{ color: t.color, letterSpacing: "0.10em",
                          fontWeight: 700 }}>
              {t.tier.toUpperCase()}
            </div>
            <p className="text-[12px] mt-2"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
              {t.desc}
            </p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}


// ===========================================================================
// 9. RIGOROUS TESTING & VALIDATION
// ===========================================================================
const VALIDATION_STATS = [
  { value: "180+",  label: "BACKEND REGRESSION TESTS" },
  { value: "9",     label: "DETECTOR-LEVEL TEST SUITES" },
  { value: "100%",  label: "SCORE REPRODUCIBILITY" },
];

function RigorousTesting() {
  return (
    <SectionShell num="9" title="Rigorous testing &amp; validation"
                  testid="section-validation">
      <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
        Every formula change ships behind a regression test. We re-run the
        complete suite before every methodology bump — if a published score
        would change, the version stamps it explicitly. We treat scoring
        determinism as a load-bearing invariant, not a nice-to-have.
      </p>
      <div className="qt-card mt-5 grid grid-cols-1 md:grid-cols-3 gap-6"
           data-testid="validation-stats"
           style={{ padding: "22px 24px",
                    background: "rgba(4,9,20,0.55)",
                    borderColor: "var(--qt-border-soft)" }}>
        {VALIDATION_STATS.map((s) => (
          <div key={s.label}>
            <div className="qt-mono"
                 style={{ color: "var(--qt-cyan)",
                          fontSize: 32, fontWeight: 700,
                          letterSpacing: "-0.03em",
                          lineHeight: 1 }}>
              {s.value}
            </div>
            <div className="qt-mono text-[10px] mt-2"
                 style={{ color: "var(--qt-text-3)",
                          letterSpacing: "0.16em" }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <a href="mailto:security@qortrace.com?subject=Methodology%20bug%20report"
           data-testid="validation-disclosure-link"
           className="qt-card flex items-center justify-between transition-all"
           style={{ padding: 14, textDecoration: "none",
                    background: "rgba(4,9,20,0.55)",
                    borderColor: "var(--qt-border-soft)" }}>
          <div className="flex items-center gap-2.5">
            <ShieldAlert size={14} style={{ color: "var(--qt-amber)" }} />
            <div>
              <div className="qt-mono"
                   style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                Responsible disclosure
              </div>
              <div className="qt-mono text-[11px]"
                   style={{ color: "var(--qt-text-3)" }}>
                Found a methodology bug? security@qortrace.com
              </div>
            </div>
          </div>
          <ChevronRight size={13} style={{ color: "var(--qt-text-3)" }} />
        </a>
        <a href="mailto:methodology@qortrace.com?subject=Methodology%20peer%20review"
           data-testid="validation-peer-review-link"
           className="qt-card flex items-center justify-between transition-all"
           style={{ padding: 14, textDecoration: "none",
                    background: "rgba(4,9,20,0.55)",
                    borderColor: "var(--qt-border-soft)" }}>
          <div className="flex items-center gap-2.5">
            <BookOpen size={14} style={{ color: "var(--qt-cyan)" }} />
            <div>
              <div className="qt-mono"
                   style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                Peer-review channel
              </div>
              <div className="qt-mono text-[11px]"
                   style={{ color: "var(--qt-text-3)" }}>
                Cryptographers + standards bodies — methodology@qortrace.com
              </div>
            </div>
          </div>
          <ChevronRight size={13} style={{ color: "var(--qt-text-3)" }} />
        </a>
      </div>
    </SectionShell>
  );
}


// ===========================================================================
// 10. LIVE METHODOLOGY UPDATES
// ===========================================================================
function LiveUpdates() {
  return (
    <SectionShell num="10" title="Live methodology updates"
                  testid="section-live-updates">
      <div className="qt-card grid grid-cols-1 lg:grid-cols-3 gap-6"
           style={{ padding: "22px 24px",
                    background: "rgba(4,9,20,0.55)",
                    borderColor: "var(--qt-border-soft)" }}>
        <div>
          <div className="qt-label">CURRENT</div>
          <div className="flex items-center gap-2 mt-1.5">
            <span style={{ width: 8, height: 8, borderRadius: 999,
                           background: "#1FDD83",
                           boxShadow: "0 0 12px rgba(123,217,168,0.7)",
                           display: "inline-block" }} />
            <span data-testid="live-current-version"
                  className="qt-mono"
                  style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>
              {METHOD_VERSION}
            </span>
          </div>
          <div className="qt-mono text-[11px] mt-1.5"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}>
            PUBLISHED {PUBLISHED_DATE}
          </div>
        </div>
        <div>
          <div className="qt-label">PLANNED · {NEXT_VERSION.toUpperCase()}</div>
          <ul className="qt-mono text-[12px] mt-2 space-y-1"
              style={{ color: "var(--qt-text-2)", letterSpacing: "0.02em" }}
              data-testid="live-planned-list">
            <li>· Aptos / Sui Move primitives + score modifiers</li>
            <li>· Hybrid PQ + ECDSA signature attestation</li>
            <li>· KMS / HSM inventory probe (AWS, Azure, GCP)</li>
            <li>· Certificate-chain crawler for enterprise PKI</li>
          </ul>
          <div className="qt-mono text-[10px] mt-2"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
            ETA · {NEXT_TARGET_ETA}
          </div>
        </div>
        <div>
          <div className="qt-label">SUBSCRIBE</div>
          <p className="text-[12px] mt-1.5"
             style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
            Get notified when the methodology version bumps. We email a
            human-readable diff + the previous version's permanent URL.
          </p>
          <a href={`${BACKEND}/api/methodology.rss`}
             target="_blank" rel="noopener noreferrer"
             data-testid="live-subscribe-cta"
             className="qt-btn qt-btn-secondary inline-flex items-center gap-2 mt-3"
             style={{ fontSize: 12, padding: "6px 14px" }}>
            <Bell size={11} /> Subscribe via RSS
          </a>
          <a href="mailto:methodology@qortrace.com?subject=Subscribe%20to%20methodology%20updates&body=Please%20notify%20me%20when%20the%20QorTrace%20methodology%20version%20bumps."
             data-testid="live-subscribe-email"
             className="qt-mono text-[11px] inline-flex items-center gap-1.5 mt-2 ml-3"
             style={{ color: "var(--qt-text-3)",
                      letterSpacing: "0.06em",
                      textDecoration: "underline",
                      textUnderlineOffset: 3 }}>
            or email
          </a>
        </div>
      </div>

      {/* Iter275.3 — Auto-generated "Recently shipped" band. Pulls
          /api/methodology/recent-changes which reuses the same parser
          as Qelli's knowledge snapshot — so this stays in lockstep
          with whatever just hit main. */}
      <RecentlyShipped />
    </SectionShell>
  );
}


function RecentlyShipped() {
  const [entries, setEntries] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND}/api/methodology/recent-changes`, { params: { limit: 5 } })
      .then((r) => { if (!cancelled) setEntries(r.data?.entries || []); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "error"); });
    return () => { cancelled = true; };
  }, []);
  if (err || !entries || entries.length === 0) return null;

  return (
    <div className="qt-card mt-6"
         data-testid="methodology-recently-shipped"
         style={{
           padding: "22px 24px",
           background: "rgba(4,9,20,0.55)",
           borderColor: "var(--qt-border-soft)",
         }}>
      <div className="flex items-center gap-2 mb-3">
        <span style={{
          display: "inline-block", width: 8, height: 8, borderRadius: 999,
          background: "var(--qt-cyan)",
          boxShadow: "0 0 12px rgba(0,229,255,0.7)",
        }} />
        <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
          RECENTLY SHIPPED · AUTO-GENERATED FROM CHANGELOG
        </div>
      </div>
      <ul className="space-y-3" data-testid="methodology-recently-shipped-list">
        {entries.map((e) => (
          <li key={`${e.date}-${e.title}`}
              className="border-l-2 pl-3"
              style={{ borderColor: "var(--qt-border-strong)" }}>
            <div className="qt-mono text-[10px]"
                 style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
              {e.date}
            </div>
            <div className="text-sm font-medium mt-0.5"
                 style={{ color: "var(--qt-text-1)" }}>
              {e.title}
            </div>
            {e.summary && (
              <div className="text-xs mt-1 leading-snug"
                   style={{ color: "var(--qt-text-3)", maxWidth: 720 }}>
                {e.summary}
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="qt-mono text-[10px] mt-4"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
        UPDATES AUTOMATICALLY ON EVERY PUSH · NIGHTLY RECONCILIATION AT 02:00 UTC
      </div>
    </div>
  );
}


// ===========================================================================
// 11. COMPLIANCE RECEIPT — downloadable PDF stamping reviewer + content hash
// ===========================================================================
function ComplianceReceiptSection() {
  const [name, setName]     = useState("");
  const [email, setEmail]   = useState("");
  const [org, setOrg]       = useState("");
  const [title, setTitle]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [issued, setIssued] = useState(null); // {filename, serial, hash}

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setIssued(null);
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND}/api/methodology/compliance-receipt`,
        { name: name.trim(), email: email.trim(),
          organization: org.trim() || null,
          title: title.trim() || null },
        { responseType: "blob" },
      );
      // Pull the receipt metadata from response headers BEFORE we discard
      // the blob into a download anchor.
      const serial = r.headers["x-qortrace-receipt-serial"] || "";
      const chash  = r.headers["x-qortrace-content-hash"] || "";
      const fname  = `qortrace-methodology-receipt-${serial.slice(0, 8) || "receipt"}.pdf`;

      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);

      setIssued({ filename: fname, serial, hash: chash });
    } catch (e) {
      // axios with responseType:'blob' wraps detail inside the blob — best-effort parse.
      let msg = "Failed to issue receipt.";
      const blob = e?.response?.data;
      if (blob && typeof blob.text === "function") {
        try {
          const txt = await blob.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch (_) { /* keep default */ }
      } else if (e?.message) {
        msg = e.message;
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell num="11" title="Methodology compliance receipt"
                  testid="section-compliance-receipt">
      <p style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
        Auditors and compliance reviewers — generate a stamped PDF receipt
        proving you accessed and read this version of the methodology.
        Useful for SOC 2 / ISO 27001 / FFIEC workpapers. Receipt includes
        your name, organization, the methodology version, a SHA-256 hash
        of the canonical content, and a unique serial number.
      </p>
      <div className="qt-card mt-5"
           data-testid="receipt-card"
           style={{ padding: 22,
                    background: "rgba(4,9,20,0.55)",
                    borderColor: "var(--qt-border-soft)" }}>
        <div className="flex items-center gap-2 mb-4">
          <Award size={16} style={{ color: "var(--qt-cyan)" }} />
          <div className="qt-mono"
               style={{ color: "#fff", fontWeight: 700,
                        fontSize: 14, letterSpacing: "-0.005em" }}>
            Issue receipt
          </div>
        </div>
        <form onSubmit={submit}
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
              data-testid="receipt-form">
          <Field label="NAME" required>
            <input type="text" required value={name}
                   onChange={(e) => setName(e.target.value)}
                   data-testid="receipt-name"
                   placeholder="Jane Reviewer"
                   className="qt-input" />
          </Field>
          <Field label="EMAIL" required>
            <input type="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   data-testid="receipt-email"
                   placeholder="jane@firm.com"
                   className="qt-input" />
          </Field>
          <Field label="ORGANIZATION (OPTIONAL)">
            <input type="text" value={org}
                   onChange={(e) => setOrg(e.target.value)}
                   data-testid="receipt-org"
                   placeholder="Compliance & Co."
                   className="qt-input" />
          </Field>
          <Field label="TITLE (OPTIONAL)">
            <input type="text" value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   data-testid="receipt-title"
                   placeholder="Senior IT Auditor"
                   className="qt-input" />
          </Field>
          <div className="md:col-span-2 mt-1 flex items-center gap-3 flex-wrap">
            <button type="submit" disabled={busy || !name.trim() || !email.trim()}
                    data-testid="receipt-submit"
                    className="qt-btn qt-btn-primary inline-flex items-center gap-2"
                    style={{ fontSize: 13, padding: "8px 18px",
                             opacity: busy ? 0.6 : 1 }}>
              {busy ? <Loader2 size={13} className="animate-spin" />
                    : <Download size={13} />}
              {busy ? "Issuing receipt…" : "Generate compliance receipt"}
            </button>
            <span className="qt-mono text-[11px]"
                  style={{ color: "var(--qt-text-3)",
                           letterSpacing: "0.06em" }}>
              PDF · SHA-256-stamped · Saved for audit trail
            </span>
          </div>
          {error && (
            <div className="md:col-span-2 qt-mono text-[12px] flex items-center gap-2"
                 data-testid="receipt-error"
                 style={{ color: "#FF6E6E", letterSpacing: "0.04em" }}>
              <AlertOctagon size={11} /> {error}
            </div>
          )}
          {issued && (
            <div className="md:col-span-2 qt-mono text-[12px]"
                 data-testid="receipt-success"
                 style={{ color: "#1FDD83",
                          background: "rgba(123,217,168,0.08)",
                          border: "1px solid rgba(123,217,168,0.30)",
                          padding: "10px 14px", borderRadius: 4,
                          letterSpacing: "0.04em" }}>
              <Check size={11} className="inline -mt-0.5 mr-1.5" />
              Receipt downloaded · {issued.filename}
              <div className="text-[10px] mt-1.5"
                   style={{ color: "var(--qt-text-3)" }}>
                SERIAL · {issued.serial}{issued.hash ? ` · HASH-PREFIX · ${issued.hash}` : ""}
              </div>
            </div>
          )}
        </form>
        <div className="qt-mono text-[11px] mt-4 flex items-start gap-2"
             style={{ color: "var(--qt-text-3)", letterSpacing: "0.03em",
                      lineHeight: 1.5 }}>
          <Lock size={11} style={{ flexShrink: 0, marginTop: 1.5 }} />
          <span>
            We store your name, email, organization, and the receipt serial
            so issuance is auditable. We won't add you to marketing lists —
            opt in separately via the changelog subscribe link.
          </span>
        </div>
      </div>
    </SectionShell>
  );
}


function Field({ label, children, required }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="qt-label"
            style={{ letterSpacing: "0.16em" }}>
        {label}{required && <span style={{ color: "#FF6E6E" }}> *</span>}
      </span>
      {children}
    </label>
  );
}


// ===========================================================================
// 14. REFERENCES (rich, with external links)
// ===========================================================================
const REFERENCES = [
  { label: "NIST FIPS 203 — Module-Lattice Key-Encapsulation Mechanism (ML-KEM)", url: "https://csrc.nist.gov/pubs/fips/203/final" },
  { label: "NIST FIPS 204 — Module-Lattice Digital Signature Algorithm (ML-DSA)", url: "https://csrc.nist.gov/pubs/fips/204/final" },
  { label: "NIST FIPS 205 — Stateless Hash-Based Digital Signature Algorithm (SLH-DSA)", url: "https://csrc.nist.gov/pubs/fips/205/final" },
  { label: "NIST SP 800-57 — Recommendation for Key Management", url: "https://csrc.nist.gov/pubs/sp/800/57/pt1/r5/final" },
  { label: "NSA CNSA 2.0 PQC migration timeline (2030 / 2035)", url: "https://media.defense.gov/2025/May/30/2003728741/-1/-1/0/CSA_CNSA_2.0_ALGORITHMS.PDF" },
  { label: "BIP-340 — Schnorr signatures over secp256k1", url: "https://github.com/bitcoin/bips/blob/master/bip-0340.mediawiki" },
  { label: "EIP-712, EIP-1271, EIP-2537, EIP-7702", url: "https://eips.ethereum.org/" },
  { label: "ENISA — Post-Quantum Cryptography integration study", url: "https://www.enisa.europa.eu/publications/post-quantum-cryptography-integration-study" },
  { label: "FFIEC IT Examination Handbook · Information Security", url: "https://ithandbook.ffiec.gov/it-booklets/information-security/" },
  { label: "ISO/IEC 27001:2022 — Annex A.10 cryptographic controls", url: "https://www.iso.org/standard/27001" },
  { label: "EU DORA — Digital Operational Resilience Act", url: "https://www.eiopa.europa.eu/digital-operational-resilience-act-dora_en" },
  { label: "BIS — Prudential treatment of cryptoasset exposures", url: "https://www.bis.org/bcbs/publ/d545.htm" },
  { label: "NIST CMVP — FIPS 140-3 Cryptographic Module Validation", url: "https://csrc.nist.gov/projects/cryptographic-module-validation-program" },
];

function References() {
  return (
    <SectionShell num="14" title="References" testid="section-references">
      <ul className="space-y-1.5 mt-3" data-testid="references-list">
        {REFERENCES.map((r, i) => (
          <li key={r.url} className="flex items-baseline gap-3">
            <span className="qt-mono text-[11px] shrink-0"
                  style={{ color: "var(--qt-text-3)",
                           letterSpacing: "0.06em",
                           minWidth: 28 }}>
              [{String(i + 1).padStart(2, "0")}]
            </span>
            <a href={r.url} target="_blank" rel="noopener noreferrer"
               data-testid={`reference-link-${i + 1}`}
               className="qt-mono text-[13px] inline-flex items-center gap-1.5 transition-colors"
               style={{ color: "var(--qt-text-2)",
                        letterSpacing: "0.01em",
                        textDecoration: "underline",
                        textDecorationColor: "rgba(102,224,255,0.20)",
                        textUnderlineOffset: 3 }}>
              {r.label}
              <ExternalLink size={10}
                            style={{ color: "var(--qt-cyan)", flexShrink: 0 }} />
            </a>
          </li>
        ))}
      </ul>
    </SectionShell>
  );
}


// ===========================================================================
// SHARED — section shell, table, formula, citation, version history
// ===========================================================================
function SectionShell({ num, title, children, testid }) {
  return (
    <section className="mt-16" data-testid={testid}>
      <div className="flex items-baseline gap-3">
        <span className="qt-mono"
              style={{ color: "var(--qt-cyan)", fontSize: 11,
                       letterSpacing: "0.20em", fontWeight: 700 }}>
          §{num.toString().padStart(2, "0")}
        </span>
        <h2 className="qt-h2 text-2xl"
            style={{ letterSpacing: "-0.01em" }}>
          {title}
        </h2>
      </div>
      <div style={{ height: 1, background: "var(--qt-border)",
                    marginTop: 12, marginBottom: 16 }} />
      {children}
    </section>
  );
}


function Table({ head, rows }) {
  return (
    <div className="qt-card overflow-x-auto mt-3"
         style={{ padding: 0, background: "rgba(4,9,20,0.55)",
                  borderColor: "var(--qt-border-soft)" }}>
      <table className="text-sm w-full">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--qt-border)" }}>
            {head.map((h, i) => (
              <th key={i}
                  className="qt-label text-left py-3 px-4"
                  style={{ letterSpacing: "0.16em" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}
                style={{ borderTop: i === 0 ? "none"
                                            : "1px solid var(--qt-border-soft)" }}>
              {r.map((c, j) => (
                <td key={j}
                    className={`py-3 px-4 ${j === 0 ? "qt-mono" : ""}`}
                    style={{ color: j === 0 ? "#fff" : "var(--qt-text-2)",
                             fontSize: j === 0 ? 13 : 13 }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function Formula({ rows, total }) {
  return (
    <div className="qt-card overflow-x-auto"
         style={{ padding: 0, background: "rgba(4,9,20,0.65)",
                  borderColor: "var(--qt-border-soft)" }}>
      <table className="text-sm w-full">
        <thead>
          <tr style={{ borderBottom: "1px solid var(--qt-border)" }}>
            <th className="qt-label text-left py-3 px-4"
                style={{ letterSpacing: "0.16em" }}>FACTOR</th>
            <th className="qt-label text-left py-3 px-4"
                style={{ letterSpacing: "0.16em" }}>RANGE</th>
            <th className="qt-label text-left py-3 px-4"
                style={{ letterSpacing: "0.16em" }}>RULE</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}
                style={{ borderTop: i === 0 ? "none"
                                            : "1px solid var(--qt-border-soft)" }}>
              <td className="py-3 px-4 font-semibold"
                  style={{ color: "#fff", fontSize: 13 }}>{r[0]}</td>
              <td className="py-3 px-4 qt-mono"
                  style={{ color: "var(--qt-cyan)", fontSize: 13 }}>{r[1]}</td>
              <td className="py-3 px-4 qt-mono text-xs"
                  style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>{r[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ borderTop: "1px solid var(--qt-border)",
                    padding: "10px 16px",
                    color: "var(--qt-amber)",
                    fontFamily: "var(--qt-font-mono, monospace)",
                    fontSize: 11, letterSpacing: "0.10em",
                    fontWeight: 700 }}>
        {total}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Citation block (preserved from v0.1, unchanged behaviour)
// ---------------------------------------------------------------------------
function CitationBlock() {
  const [copied, setCopied] = useState("");
  const cite = (kind, txt) => {
    navigator.clipboard?.writeText(txt);
    setCopied(kind);
    setTimeout(() => setCopied(""), 1600);
  };

  const apa     = `QorTrace. (${PUBLISHED_DATE.slice(0, 4)}). QorTrace Scoring Methodology (Version ${METHOD_VERSION}). QorTrace. https://qortrace.com/methodology`;
  const mla     = `QorTrace. "QorTrace Scoring Methodology." Version ${METHOD_VERSION}, ${PUBLISHED_DATE}, qortrace.com/methodology.`;
  const chicago = `QorTrace. ${PUBLISHED_DATE.slice(0, 4)}. "QorTrace Scoring Methodology." Version ${METHOD_VERSION}. https://qortrace.com/methodology.`;
  const bibtex  = `@techreport{qortrace_method_${METHOD_VERSION.replace(/[^a-z0-9]/gi, "")},
  title  = {QorTrace Scoring Methodology},
  author = {{QorTrace}},
  year   = {${PUBLISHED_DATE.slice(0, 4)}},
  month  = {${PUBLISHED_DATE.slice(5, 7)}},
  type   = {Methodology Specification},
  number = {${METHOD_VERSION}},
  url    = {https://qortrace.com/methodology}
}`;

  const items = [
    { id: "apa",     label: "APA",     text: apa },
    { id: "mla",     label: "MLA",     text: mla },
    { id: "chicago", label: "Chicago", text: chicago },
    { id: "bibtex",  label: "BibTeX",  text: bibtex },
  ];

  return (
    <div className="space-y-3 mt-3" data-testid="citation-block">
      {items.map((it) => (
        <div key={it.id} className="qt-card"
             style={{ padding: 14, background: "rgba(4,9,20,0.65)",
                      borderColor: "var(--qt-border-soft)" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="qt-label">{it.label}</span>
            <button
              data-testid={`copy-${it.id}`}
              onClick={() => cite(it.id, it.text)}
              className="qt-btn qt-btn-ghost"
              style={{ fontSize: 11, padding: "4px 10px" }}>
              {copied === it.id
                ? (<><Check size={12}/> COPIED</>)
                : (<><Copy size={12}/> COPY</>)}
            </button>
          </div>
          <pre className="qt-mono text-xs whitespace-pre-wrap break-words"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.6,
                        margin: 0 }}>
            {it.text}
          </pre>
        </div>
      ))}
      <div className="qt-mono text-xs"
           style={{ color: "var(--qt-text-3)" }}>
        Stable URL: <span style={{ color: "var(--qt-cyan)" }}>
          https://qortrace.com/methodology
        </span> · Methodology version stamps remain reproducible across
        future revisions.
      </div>
    </div>
  );
}


const VERSIONS = [
  {
    id: METHOD_VERSION,
    slug: "v0.2",
    date: PUBLISHED_DATE,
    status: "current",
    title: "Institutional crypto-inventory expansion",
    changes: [
      "Threat model expanded beyond Shor/Grover to 6 institutional surfaces (HNDL, blockchain, KMS/HSM, PKI, vendor supply chain, compliance windows).",
      "Compliance & standards alignment grid added — 12 standards with direct external citations (NIST FIPS 203/204/205, NSA CNSA 2.0, ISO 27001, SOC 2, FFIEC, FIPS 140-3, ENISA, EU DORA, BIS).",
      "Page restructured into 13 numbered sections with IDE/whitepaper aesthetic.",
      "Detector inventory expanded with example trigger snippets per detector.",
      "References list includes external links to every cited standard.",
      "Methodology compliance receipt — downloadable SHA-256-stamped PDF for SOC 2 / ISO 27001 / FFIEC workpapers.",
    ],
  },
  {
    id: "qortrace-method-v0.1",
    slug: "v0.1",
    date: "2026-01-15",
    status: "previous",
    title: "Initial public methodology release",
    changes: [
      "BTC / UTXO-family scoring engine: 5 factors (pubkey exposure, address reuse, balance at risk, dormancy, address-type modifier).",
      "EVM-family scoring engine: 5 factors (Ethereum, BSC, Base, Polygon, Arbitrum, Optimism, Tron).",
      "Solana scoring engine — recognizes structural address-as-pubkey exposure (60-point baseline).",
      "EIP-7702 delegation detection (Pectra, May 2025) — preserves EOA classification.",
      "9 Solidity detectors: ecrecover_usage, eip712_signing, permit_pattern, bls_precompile, hardcoded_signer, multisig_threshold, replay_protection, upgradeable_proxy, eip1271_signer.",
    ],
  },
];

function VersionHistory() {
  return (
    <div className="mt-3" data-testid="version-history">
      <div className="relative">
        <div style={{ position: "absolute", left: 7, top: 8, bottom: 8,
                      width: 1, background: "var(--qt-border)" }} />
        <div className="space-y-5">
          {VERSIONS.map((v) => (
            <div key={v.id} className="relative pl-8"
                 data-testid={`version-${v.id}`}>
              <span style={{ position: "absolute", left: 1, top: 6,
                             width: 13, height: 13, borderRadius: 999,
                             background: v.status === "current" ? "#1FDD83" : "var(--qt-text-3)",
                             border: v.status === "current"
                                     ? "2px solid rgba(123,217,168,0.30)"
                                     : "2px solid rgba(120,130,150,0.30)",
                             boxShadow: v.status === "current"
                                        ? "0 0 12px rgba(123,217,168,0.5)" : "none" }} />
              <div className="qt-card" style={{ padding: 16,
                       background: "rgba(4,9,20,0.55)",
                       borderColor: "var(--qt-border-soft)" }}>
                <div className="flex items-center gap-3 flex-wrap">
                  {v.status === "current" ? (
                    <span className="qt-mono"
                          style={{ color: "var(--qt-cyan)",
                                   fontWeight: 700, fontSize: 13,
                                   letterSpacing: "-0.01em" }}>
                      {v.id}
                    </span>
                  ) : (
                    <a href={`/methodology/${v.slug}`}
                       data-testid={`version-link-${v.slug}`}
                       className="qt-mono"
                       style={{ color: "var(--qt-cyan)",
                                fontWeight: 700, fontSize: 13,
                                letterSpacing: "-0.01em",
                                textDecoration: "underline",
                                textUnderlineOffset: 3 }}>
                      {v.id} →
                    </a>
                  )}
                  <span className="qt-mono text-[11px]"
                        style={{ color: "var(--qt-text-3)",
                                 letterSpacing: "0.06em" }}>
                    {v.date}
                  </span>
                  {v.status === "current" && (
                    <span className="qt-mono text-[10px] px-2 py-0.5 rounded"
                          style={{ border: "1px solid #1FDD83",
                                   color: "#1FDD83",
                                   letterSpacing: "0.16em",
                                   fontWeight: 700 }}>
                      CURRENT
                    </span>
                  )}
                </div>
                {v.title && (
                  <div className="text-sm mt-2"
                       style={{ color: "#fff", fontWeight: 600 }}>
                    {v.title}
                  </div>
                )}
                <ul className="mt-3 text-[13px] space-y-1.5"
                    style={{ color: "var(--qt-text-2)",
                             listStyle: "disc",
                             paddingLeft: "1.1rem", lineHeight: 1.55 }}>
                  {v.changes.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="qt-mono text-xs mt-4"
           style={{ color: "var(--qt-text-3)" }}>
        Older versions live at{" "}
        <a href="/methodology/v0.1"
           data-testid="version-history-archive-link"
           style={{ color: "var(--qt-cyan)",
                    textDecoration: "underline",
                    textUnderlineOffset: 3 }}>
          /methodology/v0.1
        </a>{" "}— click any non-current row above to view its archive.
      </div>
    </div>
  );
}
